import { isTravelTypeWithHousing } from '@repo/utils'
import React, { HTMLProps } from 'react'
import { useIntl } from 'react-intl'

import {
  formatHouseAddress,
  getClientProposalFallbackSvg,
} from './client-proposal-card.utils'
import { PictureCarousel } from '../picture-carousel/PictureCarousel'
import Icon from '../../atoms/icons/icon/Icon'
import { useTravelTypeContext } from '../../contexts'
import { Separator } from '../../../shadcn/components/ui/separator'
import { cn } from '../../../shadcn/utils'

export type ClientProposalCardProps = {
  nudge?: React.ReactNode
  region: string
  department?: string
  title: string
  houseId?: string
  onHouseTitleClick?: () => void
  dates: {
    startDate: Date
    endDate?: Date
  }
  disponibility?: React.ReactNode
  price: React.ReactNode
  footer?: React.ReactNode
  imageUrls: string[]
  hoverStyle?: 'none' | 'hovered'
  containerProps?: HTMLProps<HTMLDivElement>
  isClientProposalCancelled: boolean
}

export const ClientProposalCard = ({
  nudge,
  region,
  department,
  title,
  dates,
  onHouseTitleClick,
  disponibility,
  price,
  footer,
  imageUrls,
  hoverStyle = 'none',
  containerProps,
  isClientProposalCancelled,
}: ClientProposalCardProps) => {
  const intl = useIntl()
  const hasFooter = !!footer

  const { travelType } = useTravelTypeContext()

  return (
    <div className='@container' {...containerProps}>
      <div className='relative'>
        {nudge && <div className='absolute -top-4 right-0 z-10'>{nudge}</div>}
      </div>
      <div
        className={cn(
          'flex flex-col overflow-hidden rounded-lg border border-grey-400 bg-white shadow',
          '@2xl:flex-row',
          {
            '@2xl:h-52': !hasFooter,
            '@2xl:h-72': hasFooter,
          },
          {
            'bg-grey-100 outline outline-1 outline-black':
              hoverStyle === 'hovered',
          },
        )}
      >
        <div
          className={cn(
            'h-36 overflow-hidden @2xl:h-full',
            '@2xl:min-w-[40%] @2xl:max-w-[40%]',
            '@4xl:min-w-[50%] @4xl:max-w-[50%]',
          )}
        >
          <PictureCarousel
            urls={imageUrls}
            fallbackSvg={getClientProposalFallbackSvg(travelType)}
          />
        </div>
        <div
          className={cn('flex flex-col gap-1 p-5', '@2xl:min-w-0 @2xl:flex-1')}
        >
          <span
            className={cn(
              'text-xs text-grey-700',
              '@2xl:text-base',
              `${isClientProposalCancelled && '!line-through'}`,
            )}
          >
            {formatHouseAddress({ department, region })}
          </span>
          <button
            onClick={() => onHouseTitleClick?.()}
            title={title}
            className={cn(
              `truncate text-left text-xl font-medium tracking-tight ${isTravelTypeWithHousing(travelType) ? 'hover:underline' : 'cursor-default'}`,
              '@2xl:min-w-0 @2xl:text-3xl',
              `${isClientProposalCancelled && '!line-through'}`,
            )}
          >
            {title}
          </button>
          <div className={cn('flex flex-col gap-2', '@2xl:flex-row')}>
            <div
              className={cn(
                'flex items-center gap-2',
                `${isClientProposalCancelled && '!line-through'}`,
              )}
            >
              <Icon
                name='calendar-empty'
                color='black'
                width={16}
                height={16}
              />
              {dates.endDate &&
              new Date(dates.startDate).getTime() !==
                new Date(dates.endDate).getTime() ? (
                <span
                  className={cn('text-xs tracking-tight', '@2xl:text-base')}
                >
                  {intl.formatDate(dates.startDate, {
                    day: 'numeric',
                    month: 'long',
                  })}{' '}
                  -{' '}
                  {intl.formatDate(dates.endDate, {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
              ) : (
                <span>
                  {intl.formatDate(dates.startDate, {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
              )}
            </div>
            <div className='flex items-center gap-2'>{disponibility}</div>
          </div>
          <div className='my-auto pt-2'>{price}</div>
          {footer && (
            <>
              <div className='my-3 flex w-full'>
                <Separator
                  orientation='vertical'
                  className='mx-auto h-[1px] w-full bg-grey-400'
                />
              </div>
              <div>{footer}</div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
